import React, { useCallback, useRef, useState, useEffect } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { getSignedUrl } from '../../../utils/fileUtils';
import { useFileViewer } from '../../FileViewer';
import type { FilePointer } from '../../../types/forms';

const DocIcon = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);

export const FileCellRenderer: React.FC<ICellRendererParams> = ({ value }) => {
  const files: FilePointer[] = Array.isArray(value) ? value : [];
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const prefetchingRef = useRef<Set<number>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { openFile } = useFileViewer();

  const prefetch = useCallback(async (id: number) => {
    if (signedUrls[id] || prefetchingRef.current.has(id)) return;
    prefetchingRef.current.add(id);
    const url = await getSignedUrl(id);
    if (url) setSignedUrls((prev) => ({ ...prev, [id]: url }));
    prefetchingRef.current.delete(id);
  }, [signedUrls]);

  const handleClick = (file: FilePointer) => {
    openFile({ fileId: file.id, filename: file.filename, mimeType: file.mime_type });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  if (files.length === 0) {
    return <span style={{ color: 'var(--text-3)' }}>—</span>;
  }

  if (files.length === 1) {
    const file = files[0];
    return (
      <span
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--accent)' }}
        onClick={() => handleClick(file)}
        onMouseEnter={() => prefetch(file.id)}
        title={file.filename}
      >
        <DocIcon />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.filename}</span>
      </span>
    );
  }

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <span
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-2)' }}
        onClick={() => { setMenuOpen(!menuOpen); files.forEach((f) => prefetch(f.id)); }}
        onMouseEnter={() => files.forEach((f) => prefetch(f.id))}
      >
        <DocIcon />
        <span>{files.length} files</span>
      </span>
      {menuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, boxShadow: 'var(--shadow-md)', padding: 4, minWidth: 180,
        }}>
          {files.map((file) => (
              <div
                key={file.id}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text-1)', borderRadius: 4 }}
                onClick={() => { handleClick(file); setMenuOpen(false); }}
                onMouseEnter={() => prefetch(file.id)}
              >
                <DocIcon /> {file.filename}
              </div>
          ))}
        </div>
      )}
    </div>
  );
};
